import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CalendarCheck,
  Flame,
  Leaf,
  Snowflake,
  Wind,
  Wrench,
} from "lucide-react";
import { PHONE_TEL } from "./Nav";

const services = [
  {
    icon: Snowflake,
    title: "AC Repair",
    body: "Fast diagnosis and repair for any make or model. When your cooling quits in a Tennessee summer, we get it back — same day.",
  },
  {
    icon: Flame,
    title: "Heating & Furnace Repair",
    body: "Furnaces, heat pumps, and everything in between. No heat is an emergency here — call any hour and we'll be there.",
  },
  {
    icon: Wrench,
    title: "HVAC Installation",
    body: "Full system replacements and new installs, sized right for your home. Honest quotes, clean work, no surprises.",
  },
  {
    icon: CalendarCheck,
    title: "Maintenance Plans",
    body: "Seasonal tune-ups that catch small problems before they become 2 AM emergencies. Keep your system running longer for less.",
  },
  {
    icon: Wind,
    title: "Ductless Mini-Splits",
    body: "Efficient zoned comfort for additions, garages, and older homes without ductwork. Quiet, sleek, and easy on the power bill.",
  },
  {
    icon: Leaf,
    title: "Indoor Air Quality",
    body: "Purifiers, humidity control, and filtration so the air inside your home is as clean as the Smoky Mountain air outside.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="relative py-24 md:py-32 border-b border-white/10 noise-overlay"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-20">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF6B00] mb-6"
            >
              What We Do
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-bold uppercase tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-tight"
            >
              Every degree
              <br />
              <span className="text-[#FF6B00]">covered.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-sm text-zinc-400 text-base"
          >
            Residential heating and cooling across Knoxville, Maryville, and East
            Tennessee — repair, install, and everything in between.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {services.map((s, i) => (
            <motion.a
              key={s.title}
              href={PHONE_TEL}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-[#050505] p-8 md:p-10 hover:bg-[#121212] transition-colors duration-500"
              data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="flex items-start justify-between mb-10">
                <s.icon
                  className="w-8 h-8 text-[#FF6B00]"
                  strokeWidth={1.75}
                />
                <ArrowUpRight className="w-5 h-5 text-zinc-600 group-hover:text-[#FF6B00] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
              </div>
              <h3 className="font-display font-semibold uppercase tracking-tight text-2xl mb-3 group-hover:text-[#FF6B00] transition-colors duration-300">
                {s.title}
              </h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                {s.body}
              </p>
              <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#FF6B00] group-hover:w-full transition-[width] duration-500" />
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
